/**
 * View component for /admin/authoring/sponsors
 *
 * Generic sponsor list view. Defaults to 'all' with:
 * this.props.dispatch(sponsorActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as sponsorActions from '../sponsorActions';

// import global components
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import sponsor components
import AdminSponsorListItem from '../components/AdminSponsorListItem.js.jsx';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import sponsor css modules
import sponsorStyles from '../sponsorModuleStyles.css';

class AdminSponsorList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(sponsorActions.fetchListIfNeeded()); // defaults to 'all'
    dispatch(sponsorActions.setFilter({query: '', sortBy: 'name'}));
    dispatch(sponsorActions.setPagination({page: 1, per: 50}));
  }

  _handleSort(sortBy) {
    const { dispatch, match, sponsorList } = this.props;
    let newFilter = sponsorList.filter;
    if(sponsorList.filter.sortBy && sponsorList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(sponsorActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(sponsorActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, sponsorList } = this.props;
    let newPagination = sponsorList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(sponsorActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(sponsorActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, sponsorList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = sponsorList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(sponsorActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: sponsorList.filter.sortBy
    }));
  }

  render() {
    const {
      location
      , paginatedList
      , sortedList
      , sponsorList
      , sponsorStore
    } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !sponsorList || !sponsorList.items || sponsorList.didInvalidate;
    const sortedEmpty = sortedList.length === 0;

    return (
      <div className="flex">
        <section className="section">
          <div className="yt-container">
            <h1> Sponsors
              <Link className="yt-btn create u-pullRight" to={'/admin/sponsors/new'}>
                <img className="-icon" src={'/img/create-white.png'}></img>
              </Link>
            </h1>
            <hr/>
            { isEmpty ?
                (
                  sponsorList && sponsorList.isFetching ?
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                  :
                    <h2>Empty.</h2>
                )
              :
                <div style={{ opacity: sponsorList.isFetching ? 0.5 : 1 }}>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={sponsorList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="content-container">
                    <div className="table-wrapper">
                      <table className="yt-table striped">
                        <caption>
                          { sponsorList.filter && sponsorList.filter.query && sponsorList.filter.query.length > 0 ?
                            <span>Filtered Sponsors &mdash; {sortedList.length} of {Object.keys(sponsorList.items).length}</span>
                            :
                            <span>All Sponsors &mdash; {Object.keys(sponsorList.items).length}</span>
                          }
                          <div className="per-page-select u-pullRight">
                            <label>Show per page: </label>
                            <select
                              name="numPerPage"
                              onChange={(e) => this._setPerPage(e.target.value)}
                              value={this.state.perPage}
                              >
                              <option value={25}> 25 </option>
                              <option value={50}> 50 </option>
                              <option value={100}> 100 </option>
                            </select>
                          </div>
                        </caption>
                        <thead>
                          <tr>
                            <th style={{width: '64px'}}></th>
                            <th className="sortable" onClick={()=> this._handleSort('name')}>
                              Name
                              {sponsorList.filter.sortBy === 'name' ?
                                <i className="ion ion-ios-arrow-down" />
                                : sponsorList.filter.sortBy === '-name' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_30 sortable" onClick={()=> this._handleSort('updated')}>
                              Last Modified
                              {sponsorList.filter.sortBy === 'updated' ?
                                <i className="ion ion-ios-arrow-down" />
                                : sponsorList.filter.sortBy === '-updated' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            paginatedList.map((sponsor, i) =>
                            <AdminSponsorListItem
                              key={sponsor._id + i}
                              sponsor={sponsor}
                              />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        pagination={sponsorList.pagination}
                        setPagination={this._handleSetPagination}
                        totalPages={ Math.floor(sortedList.length / sponsorList.pagination.per) + 1}
                        />
                    }
                  </div>
                  </div>
                </div>
            }
          </div>
        </section>
      </div>
    )
  }
}

AdminSponsorList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */
  const sponsorStore = store.sponsor;
  const sponsorList = sponsorStore.lists ? sponsorStore.lists.all : null;
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (sponsorList) {

    const pagination = sponsorList.pagination;
    const filter = sponsorList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = sponsorList && sponsorList.items ? sponsorList.items.filter((sponsorId) => {
      return filterUtils.filterSponsor(queryTestString, sponsorStore.byId[sponsorId]);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = sponsorStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'logo':
        sortedList = _.orderBy(sortedList, [item => item.logo.toLowerCase()], ['asc']);
        break;
      case '-logo':
        sortedList = _.orderBy(sortedList, [item => item.logo.toLowerCase()], ['desc']);
        break;
      case 'name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
        break;
      case '-name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['desc']);
        break;
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    paginatedList
    , sortedList
    , sponsorList
    , sponsorStore
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSponsorList)
);



// WEBPACK FOOTER //
// modules/sponsor/views/AdminSponsorList.js.jsx